import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonFunctions } from '@shared/common-functions';
import { RE_SCHEDULE_WARNING_MESSAGE, SCHEDULE_OPTION_TITLE, TimeOption } from '@shared/constant/shared.constant';
import { Dropdown } from 'primeng/dropdown';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DateTime } from 'luxon';
@Component({
  selector: 'app-schedule-date-time-option',
  templateUrl: './schedule-date-time-option.component.html',
  styleUrls: ['./schedule-date-time-option.component.scss'],
})
export class ScheduleDateTimeOptionComponent implements OnInit, OnChanges {
  @ViewChild('scheduleTime') scheduleTime!: Dropdown;
  scheduleDateTimeFormGroup: FormGroup;
  @Input() bookingType: number;
  @Input() bookingEndDate: any = '';
  @Output() schdeuleDateTimeData = new EventEmitter<{}>();
  @Input() isSubmitButtonClicked: boolean = false;
  @Input() isScheduleCancelBookingMessage: boolean = false;
  @Input() timeZone: string = '';
  @Input() bookingScheduledDate:string = '';
  minSelectableDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  timeOptions: TimeOption[] = [];
  maxSelectableDate: Date | null = null;
  scheduleOptionTitle = SCHEDULE_OPTION_TITLE;
  formattedDateTime: string = '';
  reScheduleWarningMessage: string = RE_SCHEDULE_WARNING_MESSAGE;
  constructor(
    private readonly fb: FormBuilder,
    public readonly config: DynamicDialogConfig,
  ) { }

  ngOnInit(): void {
    this.scheduleDateTimeFormGroup = this.fb.group({
      scheduleCancelOption: [null],
      scheduleDate: [null],
      scheduleTime: [null],
    });

    const currentDate = new Date();
    const convertedDate = CommonFunctions.convertTimeBetweenZones(this.timeZone, currentDate);
    const covertedDateFormated = new Date(convertedDate); // Reset time to midnight
    covertedDateFormated.setHours(0, 0, 0, 0);
    this.minSelectableDate = covertedDateFormated;

    if (this.bookingEndDate) {
      const dt = DateTime.fromISO(this.bookingEndDate, { setZone: true });
      this.bookingEndDate = new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute);
      this.maxSelectableDate = this.bookingEndDate;
    }

    this.scheduleDateTimeFormGroup.get('scheduleDate')?.valueChanges.subscribe((selectedDate: Date) => {
      if (selectedDate) {
        const updatedDate = this.getConvertedDateWithCurrentTime(selectedDate);
        this.timeOptions = this.generateTimeOptions(updatedDate);
        this.scheduleDateTimeFormGroup.patchValue({ scheduleTime: null });
      } else {
        this.timeOptions = []; // Clear time options if date is cleared
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSubmitButtonClicked']?.currentValue) {
      this.scheduleDateTimeFormGroup?.markAllAsTouched();
    }
  }

  onDateChange(date: Date): void {
    const updatedDate = this.getConvertedDateWithCurrentTime(date);
    this.updateScheduleValidators();
    this.timeOptions = this.generateTimeOptions(updatedDate);
  }

  private getConvertedDateWithCurrentTime(date: Date): Date {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliseconds = now.getMilliseconds();
    const selectedDate = new Date(date);
    const timeZoneCurrentDate = new Date(CommonFunctions.convertTimeBetweenZones(this.timeZone, now));
    let timeZoneConvertTime = selectedDate;
    if (this.isSameDate(selectedDate, timeZoneCurrentDate)) {
      selectedDate.setHours(hours, minutes, seconds, milliseconds);
      timeZoneConvertTime = new Date(CommonFunctions.convertTimeBetweenZones(this.timeZone, selectedDate));
    }

    return timeZoneConvertTime;
  }

  isSameDate(date1: Date, date2: Date): boolean {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d1.getTime() === d2.getTime();
  }
generateTimeOptions(dateInput: Date | string): TimeOption[] {
  const options: TimeOption[] = [];

  const selectedDate = new Date(dateInput);
  const now = new Date();
  const timeZoneNow = new Date(CommonFunctions.convertTimeBetweenZones(this.timeZone, now));

  const selectedIsToday = this.isSameDate(selectedDate, timeZoneNow);

    const startHour = selectedIsToday ? timeZoneNow.getHours() + 1 : 0;

  let endHour = 24;
  if (this.maxSelectableDate && this.isSameDate(selectedDate, new Date(this.maxSelectableDate))) {
    const maxDate = new Date(this.maxSelectableDate);
    const maxHour = maxDate.getHours();

    if (this.bookingType === 2) {
      endHour = Math.max(maxHour - 1, 0); // inclusive of (maxHour - 2)
    } else {
      endHour = Math.min(maxHour + 1, 24); // default logic
    }
  }

  for (let hour = startHour; hour < endHour; hour++) {
    const hour12 = hour % 12 || 12;
    const period = hour < 12 ? 'AM' : 'PM';
    const display = `${String(hour12).padStart(2, '0')}:00 ${period}`;
    options.push({ name: display, value: display });
  }

  if (options.length > 0) {
    this.scheduleDateTimeFormGroup.get('scheduleTime')?.setValue(options[0].value);
  }

  return options;
}

  getFormattedScheduleDateTime(): string | null {
    const selectedDate: Date = this.scheduleDateTimeFormGroup.get('scheduleDate')?.value;
    const selectedTime: string = this.scheduleDateTimeFormGroup.get('scheduleTime')?.value;

    if (!selectedDate || !selectedTime) return null;

    const timeParts = selectedTime.trim().split(/[: ]/); // ['11', '00', 'AM']
    let hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const period = timeParts[2];

    if (period === 'PM' && hours < 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    const mergedDateTime = new Date(selectedDate);
    mergedDateTime.setHours(hours, minutes, 0, 0);
    const year = mergedDateTime.getFullYear();
    const month = String(mergedDateTime.getMonth() + 1).padStart(2, '0');
    const day = String(mergedDateTime.getDate()).padStart(2, '0');
    const hrs = String(mergedDateTime.getHours()).padStart(2, '0');
    const mins = String(mergedDateTime.getMinutes()).padStart(2, '0');
    const secs = String(mergedDateTime.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hrs}:${mins}:${secs}`;
  }

  updateScheduleValidators(): void {
    const form = this.scheduleDateTimeFormGroup;
    const isChecked = form.get('scheduleCancelOption')?.value;
    const scheduleDateControl = form.get('scheduleDate');
    const scheduleTimeControl = form.get('scheduleTime');
    const scheduleDate = scheduleDateControl?.value;

    if (isChecked) {
      scheduleDateControl?.setValidators([Validators.required]);
      scheduleTimeControl?.setValidators([Validators.required]);

      if (!scheduleDate) {
        this.schdeuleDateTimeData.emit({ dateTime: '', isScheduleDateTime: true });
      } else {
        if (!scheduleTimeControl?.value && this.timeOptions.length > 0) {
          scheduleTimeControl?.setValue(this.timeOptions[0].value);
        }
        this.formattedDateTime = this.getFormattedScheduleDateTime();
        this.schdeuleDateTimeData.emit({ dateTime: this.formattedDateTime, isScheduleDateTime: false });
      }
    } else {
      [scheduleDateControl, scheduleTimeControl].forEach(control => {
        control?.setValue(null);
        control?.clearValidators();
        control?.markAsUntouched();
        control?.markAsPristine();
        control?.updateValueAndValidity();
      });

      this.schdeuleDateTimeData.emit({ dateTime: '', isScheduleDateTime: false, isSubmitButtonClicked: false });
      return;
    }

    scheduleDateControl?.updateValueAndValidity();
    scheduleTimeControl?.updateValueAndValidity();
  }

  onScheduleCancelOptionChange(_event) {
    this.updateScheduleValidators();
  }

  openTimeDropdown(): void {
    this.scheduleTime.show();
  }

  onTimeChange(_event) {
    this.formattedDateTime = this.getFormattedScheduleDateTime();
    this.schdeuleDateTimeData.emit({ dateTime: this.formattedDateTime, isScheduleDateTime: false });
  }
}
